/**
 * Memory domain event schemas.
 *
 * Federated `unified_memory` consolidation and related Mycelium events.
 * HTTP bodies for `memory-consolidator` share the same payload shape as
 * `memory.consolidate_requested` events.
 *
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module memory.events
 * @since 2026-05-25
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'

/** Scope kinds aligned with `unified_memory.scope_kind` CHECK constraint. */
export const MemoryScopeKindSchema = z.enum([
  'global',
  'organization',
  'domain',
  'project',
  'task',
  'notebook',
  'specialist',
  'session',
  'user',
])

export type MemoryScopeKind = z.infer<typeof MemoryScopeKindSchema>

/**
 * Payload for `memory.consolidate_requested` and POST `/memory-consolidator`.
 * Used by MemoryConsolidatorConnector and the consolidator edge function.
 */
export const ConsolidateMemoryPayloadSchema = z.object({
  principal_id: z.string().uuid(),
  scope_kind: MemoryScopeKindSchema,
  scope_id: z.string().nullable(),
  parent_scope_kind: MemoryScopeKindSchema,
  parent_scope_id: z.string().nullable(),
  user_id: z.string().uuid(),
  org_id: z.string().uuid().nullable().optional(),
  max_rows: z.number().int().positive().max(500).optional(),
  dry_run: z.boolean().optional(),
})

export type ConsolidateMemoryPayload = z.infer<typeof ConsolidateMemoryPayloadSchema>

export const MemoryConsolidateRequestedEvent = BackboneEventSchema.extend({
  domain: z.literal('memory'),
  type: z.literal('consolidate_requested'),
  payload: ConsolidateMemoryPayloadSchema,
})

export const MemoryDomainEvents = [MemoryConsolidateRequestedEvent] as const
